import { toast as _toast } from "sonner"
import { X } from "lucide-react"
import { v4 as uuidv4 } from "uuid"

import { Icon } from "@/shared/components/Icon"
import { t } from "@/shared/i18n"
import { normalizeErrorMessage } from "./utils"

export type ToastOptions = {
  type: "success" | "info" | "warning" | "error" | "loading"
  message: string
  description?: string
  duration?: number
  dedupe?: boolean // If true, toasts with the same type, message, and description will be deduplicated
}

type ToastErrorOptions = {
  message?: string
  description?: string
}

function getToastErrorOptions(errorOptions?: ToastErrorOptions | boolean): ToastErrorOptions {
  return typeof errorOptions === "object" ? errorOptions : {}
}

function getErrorToastPayload(
  error: unknown,
  errorOptions: ToastErrorOptions
): { message: string; description?: string } {
  return {
    message: errorOptions.message ?? t("common:somethingWentWrong"),
    description: errorOptions.description ?? normalizeErrorMessage(error),
  }
}

const DEFAULT_TOAST_DURATION = 10_000

/**
 * Sonner only delivers a toast to the <Toaster /> instances subscribed at the moment the toast is
 * published, and never replays the ones it missed. Any toast that was created before the container
 * mounts - during bootstrap, for example - would be dropped without a trace, so those are
 * held here and flushed once there is something to render them.
 */
let isToastContainerReady = false
const pendingToasts = new Map<string, () => void>()

export function notifyToastContainerReady() {
  isToastContainerReady = true

  const queuedToasts = [...pendingToasts.values()]
  pendingToasts.clear()
  queuedToasts.forEach((showToast) => showToast())
}

/**
 * Wrapper around the sonner toast function that provides a consistent API and sensible defaults.
 * @returns Returns a function that can be called to dismiss the toast programmatically.
 */
export function toast({ type, message, description, duration, dedupe }: ToastOptions) {
  const toastId = dedupe ? [type, message, description ?? ""].join("::") : uuidv4()

  const showToast = () => {
    _toast[type](message, {
      id: toastId,
      cancel:
        type === "loading" ? undefined : (
          <Icon
            icon={X}
            className="shrink-0 cursor-pointer self-start"
            style={{ marginRight: "var(--toast-icon-margin-start)" }}
            onClick={() => _toast.dismiss(toastId)}
          />
        ),
      dismissible: false,
      description: description,
      duration: duration ?? DEFAULT_TOAST_DURATION,
    })
  }

  if (isToastContainerReady) {
    showToast()
  } else {
    pendingToasts.set(toastId, showToast)
  }

  return () => {
    // Also drop it while still queued, so dismissing a toast that has not been shown yet
    // (a loading toast whose promise settled during bootstrap) doesn't leave it stuck.
    pendingToasts.delete(toastId)
    _toast.dismiss(toastId)
  }
}

/**
 * Utility function to wrap a promise with toast notifications for success and error states.
 * @param promise The promise to wrap.
 * @param toastOptions The options for the success and error toasts.
 * @returns Returns the result of the promise if it resolves, or throws the error if it rejects.
 */
export function withToast<T>(
  promise: Promise<T>,
  toastOptions?: {
    success?: { type?: "success" | "info"; message: string; description?: string }
    error?: ToastErrorOptions | boolean
    loading?: { message: string } | boolean
    duration?: number
    dedupe?: boolean
    suppressUnauthorizedErrorToast?: boolean
  }
): Promise<T> {
  const dismissLoadingToast = toastOptions?.loading
    ? toast({
        type: "loading",
        message: toastOptions.loading === true ? t("common:pleaseWait") : toastOptions.loading.message,
        duration: Infinity,
        dedupe: toastOptions.dedupe,
      })
    : null

  return promise
    .then((data) => {
      if (toastOptions?.success) {
        toast({
          type: toastOptions.success.type ?? "success",
          message: toastOptions.success.message,
          description: toastOptions.success.description,
          duration: toastOptions.duration,
          dedupe: toastOptions.dedupe,
        })
      }
      return data
    })
    .catch((error) => {
      if (toastOptions?.error === false) {
        throw error
      }

      const errorOptions = getToastErrorOptions(toastOptions?.error)
      const errorPayload = getErrorToastPayload(error, errorOptions)

      toast({
        type: "error",
        message: errorPayload.message,
        description: errorPayload.description,
        duration: toastOptions?.duration,
        dedupe: toastOptions?.dedupe,
      })

      throw error
    })
    .finally(() => {
      if (dismissLoadingToast) {
        dismissLoadingToast()
      }
    })
}
